import { useState, useEffect } from 'react';
import { teacherAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiUserAddLine, RiEditLine, RiDeleteBinLine, RiLockPasswordLine,
  RiUserLine, RiCloseLine, RiLoaderLine, RiMailLine, RiPhoneLine,
  RiShieldUserLine,
} from 'react-icons/ri';

const TeacherManagement = () => {
  const [teachers, setTeachers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ teacherId: '', name: '', password: '', email: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => { fetchTeachers(); }, []);

  const fetchTeachers = async () => {
    try {
      const response = await teacherAPI.getAllTeachers();
      setTeachers(response.data.teachers || []);
    } catch {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await teacherAPI.updateTeacher(editingId, { name: formData.name, email: formData.email, phone: formData.phone });
        toast.success('Teacher updated successfully');
      } else {
        await teacherAPI.createTeacher(formData);
        toast.success('Teacher created successfully');
      }
      setFormData({ teacherId: '', name: '', password: '', email: '', phone: '' });
      setShowForm(false);
      setEditingId(null);
      fetchTeachers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save teacher');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;
    try {
      await teacherAPI.deleteTeacher(id);
      toast.success('Teacher deleted');
      fetchTeachers();
    } catch {
      toast.error('Failed to delete teacher');
    }
  };

  const handleResetPassword = async (id) => {
    if (!newPassword.trim()) { toast.warning('Please enter a new password'); return; }
    setResetting(true);
    try {
      await teacherAPI.resetPassword(id, newPassword);
      toast.success('Password reset successfully');
      setResetPasswordId(null);
      setNewPassword('');
    } catch {
      toast.error('Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ teacherId: '', name: '', password: '', email: '', phone: '' });
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingId(t._id);
    setFormData({ teacherId: t.teacherId, name: t.name, password: '', email: t.email || '', phone: t.phone || '' });
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 spinner" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Teacher Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">{teachers.length} teacher{teachers.length !== 1 ? 's' : ''} registered</p>
        </div>
        <button onClick={openCreate} className="btn-primary whitespace-nowrap">
          <RiUserAddLine className="w-4 h-4" /> Add Teacher
        </button>
      </div>

      {/* Form panel */}
      {showForm && (
        <div className="card p-6 mb-6 animate-fade-in-up border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-slate-800">
              {editingId ? 'Edit Teacher' : 'Create New Teacher'}
            </h2>
            <button onClick={() => setShowForm(false)} className="btn-icon btn-ghost text-slate-400">
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!editingId && (
              <>
                <div>
                  <label className="label">Teacher ID *</label>
                  <input className="input" type="text" placeholder="Unique ID" value={formData.teacherId}
                    onChange={e => setFormData({ ...formData, teacherId: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Password *</label>
                  <input className="input" type="password" placeholder="Password" value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })} required />
                </div>
              </>
            )}
            <div>
              <label className="label">Full Name *</label>
              <input className="input" type="text" placeholder="Teacher name" value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" placeholder="Email (optional)" value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className={!editingId ? '' : 'sm:col-span-2'}>
              <label className="label">Phone</label>
              <input className="input" type="tel" placeholder="Phone (optional)" value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2 flex gap-3 pt-1">
              <button type="submit" disabled={saving} className="btn-primary flex-1">
                {saving ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Saving…</> : editingId ? 'Update Teacher' : 'Create Teacher'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {teachers.length === 0 ? (
        <div className="card p-12 text-center">
          <RiUserLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No teachers yet</p>
          <p className="text-slate-400 text-sm mt-1">Click "Add Teacher" to get started</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Teacher</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">ID</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Contact</th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {teachers.map(t => (
                  <tr key={t._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <RiShieldUserLine className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-semibold text-slate-800">{t.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-md">{t.teacherId}</span>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <div className="space-y-0.5">
                        {t.email && <p className="flex items-center gap-1.5 text-slate-500 text-xs"><RiMailLine className="w-3 h-3" />{t.email}</p>}
                        {t.phone && <p className="flex items-center gap-1.5 text-slate-500 text-xs"><RiPhoneLine className="w-3 h-3" />{t.phone}</p>}
                        {!t.email && !t.phone && <span className="text-slate-400 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(t)} className="btn-icon btn-ghost text-blue-600 hover:bg-blue-50" title="Edit">
                          <RiEditLine className="w-4 h-4" />
                        </button>
                        <button onClick={() => setResetPasswordId(t._id)} className="btn-icon btn-ghost text-amber-600 hover:bg-amber-50" title="Reset Password">
                          <RiLockPasswordLine className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(t._id)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50" title="Delete">
                          <RiDeleteBinLine className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordId && (
        <div className="modal-overlay animate-fade-in">
          <div className="modal-box max-w-sm p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <RiLockPasswordLine className="w-5 h-5 text-amber-500" /> Reset Password
              </h3>
              <button onClick={() => { setResetPasswordId(null); setNewPassword(''); }} className="btn-icon btn-ghost text-slate-400">
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Enter a new password for this teacher account.</p>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="input mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button onClick={() => handleResetPassword(resetPasswordId)} disabled={resetting} className="btn-primary flex-1">
                {resetting ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Resetting…</> : 'Reset Password'}
              </button>
              <button onClick={() => { setResetPasswordId(null); setNewPassword(''); }} className="btn-secondary flex-1">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherManagement;
